import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Component that ensures the drawer's "See X products" button and "Clear All"
 * button are properly updated when filters change. This addresses an issue on
 * iOS Safari where the morph may not always correctly update content inside
 * a modal dialog opened with showModal().
 *
 * @typedef {Object} FacetsActionsRefs
 * @property {HTMLButtonElement | undefined} seeResultsButton - The "See X products" button
 * @property {HTMLButtonElement | undefined} clearButton - The "Clear All" button
 *
 * @extends {Component<FacetsActionsRefs>}
 */
class FacetsActionsUpdater extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Only add listener if we're inside a dialog (drawer)
    if (this.closest('dialog')) {
      document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Handles filter update events.
   * Forces a re-paint of the component to ensure iOS Safari updates the content.
   *
   * @param {CustomEvent} event - The filter update event
   */
  #handleFilterUpdate = (event) => {
    // Force a re-paint by toggling a class
    // This helps iOS Safari recognize the DOM changes
    this.classList.add('updating');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.remove('updating');
      });
    });

    // Update clear all button visibility
    const { clearButton } = this.refs;
    if (clearButton) {
      const shouldShowClearAll = event.detail?.queryParams
        ? [...event.detail.queryParams.entries()].some(([key]) => key.startsWith('filter.'))
        : false;
      clearButton.classList.toggle('active', shouldShowClearAll);
    }
  };
}

if (!customElements.get('facets-actions-updater')) {
  customElements.define('facets-actions-updater', FacetsActionsUpdater);
}
